import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authClient } from '../lib/authClient';
import NetworkCanvas from '../components/landing/NetworkScene';

const ProtonIcon = ({ className = 'w-full h-full' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className={className}>
    <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(60 12 12)" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(-60 12 12)" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

function InputField({ label, id, type = 'text', value, onChange, placeholder, error, autoComplete }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-muted-foreground mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`w-full px-4 py-3 rounded-xl bg-card/60 border text-foreground placeholder-slate-500 text-sm
            focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-neon transition-all
            ${error ? 'border-red-500/60 bg-red-500/5' : 'border-white/10 hover:border-white/20'}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground hover:text-muted-foreground transition-colors"
          >
            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

export default function SigninPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  function set(field) {
    return (e) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      setErrors((err) => ({ ...err, [field]: '' }));
      setApiError('');
    };
  }

  function validate() {
    const e = {};
    if (!form.email.includes('@'))  e.email    = 'Enter a valid email address';
    if (!form.password)             e.password = 'Password is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setApiError('');

    const { error } = await authClient.signIn.email({
      email:    form.email.trim().toLowerCase(),
      password: form.password,
    });

    setLoading(false);

    if (error) {
      setApiError(error.message || 'Invalid email or password.');
      return;
    }

    navigate('/home/dashboard');
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* R3F background */}
      <div className="fixed inset-0 z-0">
        <NetworkCanvas className="w-full h-full" />
      </div>

      {/* Navbar */}
      <header className="relative z-10 flex items-center justify-between px-6 h-16 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-7 h-7 text-neon group-hover:text-green-400 transition-colors">
            <ProtonIcon />
          </div>
          <span className="text-foreground font-bold text-lg">Proton</span>
          <span className="text-neon text-xs font-semibold uppercase tracking-widest ml-1 hidden sm:block">Trading</span>
        </Link>
        <p className="text-muted-foreground text-sm">
          No account yet?{' '}
          <Link to="/signup" className="text-neon hover:text-green-400 font-semibold transition-colors">
            Sign up free
          </Link>
        </p>
      </header>

      {/* Form */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold text-foreground mb-2">Welcome back</h1>
            <p className="text-muted-foreground text-sm">Sign in to your Proton Trading account.</p>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-white/8 bg-card/60 backdrop-blur-sm p-8">
            {/* Google Sign-In */}
            <button
              type="button"
              onClick={() => authClient.signIn.social({ provider: 'google', callbackURL: '/home/dashboard' })}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-white/15 hover:border-white/30 bg-transparent text-foreground font-medium text-sm transition-all hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-muted-foreground uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <InputField
                label="Email address"
                id="email"
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="jane@example.com"
                autoComplete="email"
                error={errors.email}
              />
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-muted-foreground">
                    Password
                  </label>
                  <span className="text-xs text-muted-foreground cursor-default">Forgot password?</span>
                </div>
                <InputField
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={set('password')}
                  placeholder="Your password"
                  autoComplete="current-password"
                  error={errors.password}
                />
              </div>

              {/* API error */}
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm"
                >
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  {apiError}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-neon text-black hover:bg-neon/90 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm transition-all hover:-translate-y-0.5 mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign in'}
              </button>
            </form>
          </div>

          {/* Disclaimer */}
          <p className="mt-6 text-center text-xs text-slate-600">
            Trading involves substantial risk of loss. Past performance does not guarantee future results.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
